import { DOCUMENT } from '@angular/common';
import { ElementRef, OnDestroy, OnInit, ViewChild, Component, Inject, Input, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Route, Router, Routes, UrlSegment } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { getDiseasePath } from 'shared/utils/get-disease-path';
import { diseasesToDiseasesWithSynonymString, globalDiseaseSearch } from '../disease-search';
import { Disease, Article, DiseaseTrimmed } from 'shared/types';
import { Page, Section } from 'shared/location-enums';
import { MESSAGE_KEYS, WEBSITE_SECTIONS, GRID_BREAKPOINTS } from 'shared/constants';
import { PATHS } from 'shared/paths';
import { LABELS } from '../../../../../shared/labels';
import { EspanolService } from '../../disease-es/espanol.service';
import { MessageBusService } from 'src/app/services/message-bus.service';
import { LANGUAGE } from '../../../../../shared/injection-tokens';
import { Language } from '../../../../../shared/salesforce-types';
import { ArticleService } from 'src/app/services/article.service';
import { DiseaseService } from 'src/app/services/disease.service';

const SECTION_TO_CSS_CLASS = {
    [WEBSITE_SECTIONS.home]: 'home',
    [WEBSITE_SECTIONS.diseases]: 'browse',
    [WEBSITE_SECTIONS.about]: 'about',
    [WEBSITE_SECTIONS.contactUs]: 'contact',
    [WEBSITE_SECTIONS.privacyPolicy]: 'privacyPolicy',
    [WEBSITE_SECTIONS.vulnerability]: 'vulnerabilityPolicy',
    [WEBSITE_SECTIONS.foia]: 'foia',
    [WEBSITE_SECTIONS.accessibility]: 'accessibility',
    [WEBSITE_SECTIONS.disclaimer]: 'disclaimer',
};

const SEARCH_SCROLL_LOCK_CLASS = 'gard-search-scroll-lock';

@Component({
    selector: 'main-banner',
    templateUrl: './main-banner.component.html',
    styleUrls: ['./main-banner.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class MainBannerComponent implements OnDestroy, OnInit {
    @Input() title: string;
    @Input() description: string;
    @Input() disease: Disease;
    @Input() spanishName: string | boolean = false;
    @ViewChild('desktopInput') desktopInput: ElementRef<HTMLInputElement>;
    @ViewChild('mobileInput') mobileInput: ElementRef<HTMLInputElement>;

    public synonymList: string;
    public breadcrumbs: { label: string; link: string }[] = [];
    public spanishUrlResult: string | boolean = false;
    public spanishUrl: string;
    @Input() public englishURL: string | boolean = false;
    public isSearchOpen: boolean = false;
    public headerClass: string = '';
    public pageLanguage: string;
    public englishToSpanishUrl: string;
    public spanishToEnglishUrl: string;
    public showPageTranslateBtn = false;
    public translateButtonTitle: string;
    public urlSegments: UrlSegment[];

    public diseases: DiseaseTrimmed[] = [];
    public inputTextArticle: Article;

    languageRedirect: string;
    languageBtnText: string;

    private destroySubject = new Subject();
    private readonly resultsLimit = 5;

    search: string = '';

    constructor(
        private router: Router,
        private espanolService: EspanolService,
        private messageBusService: MessageBusService,
        public route: ActivatedRoute,
        @Inject(LANGUAGE) public language: BehaviorSubject<Language>,
        public articleService: ArticleService,
        private diseaseService: DiseaseService,
        @Inject(DOCUMENT) private document: Document,
    ) {
        const sectionId = route.snapshot.data.sectionId || '';
        this.headerClass = SECTION_TO_CSS_CLASS[sectionId] || '';

        this.pageLanguage = route.snapshot.data.language;

        this.urlSegments = route.snapshot.url;

        const crumbParents = route.snapshot.data.breadcrumbParents as string[];
        if (crumbParents) {
            this.breadcrumbs = MainBannerComponent.buildBreadcrumbs(crumbParents, sectionId, this.router.config, this.language.value);
        }

        router.routeReuseStrategy.shouldReuseRoute = () => false;

        this.messageBusService
            .get(MESSAGE_KEYS.searchOpen)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(val => {
                this.isSearchOpen = !!val;
                this.updateSearchScrollLock();
                if (this.isSearchOpen) {
                    this.loadSearchData();
                    setTimeout(() => {
                        if (window.innerWidth < GRID_BREAKPOINTS.lg) {
                            this.mobileInput?.nativeElement.focus();
                        } else {
                            this.desktopInput?.nativeElement.focus();
                        }
                    });
                }
            });

        this.router.events.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            this.hide();
        });

        // TODO: refactor for getPageArticle()
        const articles = articleService.getArticles(Page.home, Section.header);

        if (this.route.snapshot.data.language === Language.En) {
            this.inputTextArticle = articles[1];

            this.languageBtnText = 'Español';
            this.languageRedirect = PATHS[Language.Es].base;
        } else {
            // Only one article is returned for Spanish page
            this.inputTextArticle = articles[1];

            this.languageBtnText = 'English';
            this.languageRedirect = '/';
        }
    }

    static buildBreadcrumbs(parentSectionIds: string[], currentSectionId: string, allRoutes: Routes, language: Language) {
        const breadcrumbs = parentSectionIds
            .map(bc => MainBannerComponent.findRouteBySectionId(allRoutes, bc, language))
            .map(r => ({
                label: this.getLabel(language, r.data.sectionId),
                link: `/${r.path}`,
            }));
        breadcrumbs.push({ label: this.getLabel(language, currentSectionId), link: '' });
        return breadcrumbs;
    }

    static getLabel(language: Language, sectionId: string) {
        return LABELS[language][sectionId] || sectionId;
    }

    static findRouteBySectionId(routes: Routes, sectionId: string, language: Language): Route {
        for (const route of routes) {
            if (route.data && route.data.sectionId === sectionId && route.data.language === language) {
                return route;
            }
            if (route.children) {
                const result = MainBannerComponent.findRouteBySectionId(route.children, sectionId, language);
                if (result) {
                    return result;
                }
            }
        }
        return undefined;
    }

    get shouldShowSearch(): boolean {
        return this.isSearchOpen || this.router.url.toLocaleLowerCase() === '/' || this.router.url.toLocaleLowerCase() === '/espanol';
    }

    get shouldShowTabs(): boolean {
        let show = false;

        if (this.router.url.toLocaleLowerCase().startsWith('/' + PATHS.diseases)) {
            const arr = this.router.url.toLocaleLowerCase().split('/');
            if (arr[1] === 'diseases' && arr[2] === String(Number(arr[2]))) {
                show = true;
            }
        }

        if (this.router.url.toLocaleLowerCase().startsWith('/' + 'espanol')) {
            const arr = this.router.url.toLocaleLowerCase().split('/');
            if (arr[1] === 'espanol' && arr[2] === String(Number(arr[2]))) {
                show = false;
            }
        }

        return show;
    }

    get shouldShowHeader(): boolean {
        let show = true;

        if (this.router.url.toLocaleLowerCase() === '/' || this.router.url.toLocaleLowerCase() === '' || this.router.url.toLocaleLowerCase() === '/espanol') {
            show = false;
        }

        return show;
    }

    get shouldBufferGenericHeaderDescription(): boolean {
        return this.headerClass === 'about' || this.headerClass === 'contact' || this.headerClass === 'browse';
    }

    get areResultsHidden(): boolean {
        return !this.search || this.search.trim().length === 0;
    }

    ngOnInit() {
        if (this.disease) {
            const diseaseName = this.disease.name;
            this.breadcrumbs[this.breadcrumbs.length - 1].label = diseaseName;
            this.spanishUrlResult = this.espanolService.getLinkToSpanish(this.disease);
            this.synonymList = this.disease.synonymsList;
            if (this.spanishUrlResult) {
                this.spanishUrl = `${this.spanishUrlResult}`;
            }
        }

        const pathArray = this.urlSegments.map(dataItem => dataItem.path);
        const path = pathArray.join('/');

        if (this.pageLanguage === Language.En) {
            for (const key in PATHS[Language.En]) {
                if (PATHS[Language.En][key] === path) {
                    this.englishToSpanishUrl = '/' + PATHS[Language.Es][key];
                    this.showPageTranslateBtn = true;
                    this.translateButtonTitle = 'Español';
                }
            }
        } else if (this.pageLanguage === Language.Es) {
            for (const key in PATHS[Language.Es]) {
                if (PATHS[Language.Es][key] === path) {
                    this.spanishToEnglishUrl = '/' + PATHS[Language.En][key];
                    this.showPageTranslateBtn = true;
                    this.translateButtonTitle = 'English';
                }
            }
        }
    }

    ngOnDestroy() {
        this.removeSearchScrollLock();
        this.destroySubject.next(undefined);
        this.destroySubject.complete();
    }

    redirectToDisease(e: PointerEvent, disease: Disease) {
        e.preventDefault();
        this.messageBusService.get(MESSAGE_KEYS.searchOpen).next(false);
        this.blurInputElements();
        this.router.navigate(getDiseasePath(disease));
    }

    hide() {
        this.isSearchOpen = false;
        this.messageBusService.get(MESSAGE_KEYS.searchOpen).next(false);
    }

    show() {
        this.isSearchOpen = true;
        this.messageBusService.get(MESSAGE_KEYS.searchOpen).next(true);
    }

    private blurInputElements() {
        this.desktopInput?.nativeElement.blur();
        this.mobileInput?.nativeElement.blur();
    }

    private updateSearchScrollLock() {
        const viewportWidth = this.document.defaultView?.innerWidth;
        const isMobileSearchOpen = this.isSearchOpen && viewportWidth !== undefined && viewportWidth < GRID_BREAKPOINTS.lg;

        this.document.documentElement.classList.toggle(SEARCH_SCROLL_LOCK_CLASS, isMobileSearchOpen);
        this.document.body.classList.toggle(SEARCH_SCROLL_LOCK_CLASS, isMobileSearchOpen);
    }

    private removeSearchScrollLock() {
        this.document.documentElement.classList.remove(SEARCH_SCROLL_LOCK_CLASS);
        this.document.body.classList.remove(SEARCH_SCROLL_LOCK_CLASS);
    }

    private updateDiseases(searchText: string) {
        this.search = searchText;

        if (!searchText.length) {
            this.diseases = [];
            return;
        }

        this.diseaseService
            .ensureTrimmedDiseases()
            .pipe(takeUntil(this.destroySubject))
            .subscribe(diseases => {
                if (this.search !== searchText) {
                    return;
                }

                this.diseases = globalDiseaseSearch(searchText, diseasesToDiseasesWithSynonymString(diseases)).slice(0, this.resultsLimit);
            });
    }

    private loadSearchData() {
        this.diseaseService.ensureTrimmedDiseases().pipe(takeUntil(this.destroySubject)).subscribe();
    }

    redirectToSearchPage() {
        this.messageBusService.get(MESSAGE_KEYS.searchOpen).next(false);
        if (this.search && this.search.trim()) {
            this.blurInputElements();

            // Determine path based on current language
            const diseasesPath = this.route.snapshot.data.language === Language.Es ? PATHS[Language.Es].diseases : PATHS[Language.En].diseases;

            this.router
                .navigate(['/' + diseasesPath], {
                    queryParams: { search: this.search },
                })
                .then()
                .catch(err => {
                    console.error(err);
                });
        }
    }
}
